import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Fixed gold banner sitting just below the sticky <Header />.
 *
 * The banner publishes its rendered height to a CSS variable
 * `--launch-banner-h` on document.documentElement so other components
 * (Hero, page wrappers) can offset their top padding while it's visible
 * and re-flow cleanly when the user dismisses it. No React state has to
 * be plumbed through props for that.
 */
export const LaunchBanner = () => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // The banner sits below the 64px header. Anything below it (Hero,
    // first sticky section) reads --launch-banner-h to know how much extra
    // top padding to apply. When dismissed we set it to 0 so the page
    // reflows tight against the header. We measure the rendered height
    // (rather than guessing) because the banner copy wraps onto two lines
    // at narrow mobile widths and would otherwise overlap the hero.
    const root = document.documentElement;
    if (dismissed) {
      root.style.setProperty('--launch-banner-h', '0px');
      return undefined;
    }
    const measure = () => {
      const el = document.querySelector('[data-testid="launch-banner"]');
      const h = el ? Math.ceil(el.getBoundingClientRect().height) + 8 : 48;
      root.style.setProperty('--launch-banner-h', `${h}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      root.style.setProperty('--launch-banner-h', '0px');
    };
  }, [dismissed]);

  if (dismissed) return null;

  return (
    <div
      className="fixed top-16 left-0 right-0 z-40"
      style={{ backgroundColor: '#F5C100' }}
      data-testid="launch-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center relative">
        <p className="text-sm sm:text-base font-semibold text-gray-900 text-center pr-8">
          Launch Special: Save $100 on the Ultimate HR Bundle. Limited time.
        </p>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss banner"
          className="absolute right-4 sm:right-6 lg:right-8 text-gray-900 hover:text-gray-700 transition-colors"
          data-testid="launch-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
